import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Logo from "../assets/composiq-mark-128.png";
import "./confirmation_page.css";
import "./trial_resend_link_page.css";

export default function TrialResendLinkPage() {
  const [searchParams] = useSearchParams();
  const presetEmail = searchParams.get("email") || "";
  const reason = searchParams.get("reason") || "";

  const inputRef = useRef(null);
  const [email, setEmail] = useState(presetEmail);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!presetEmail && inputRef.current) {
      inputRef.current.focus();
    }
  }, [presetEmail]);

  const reasonCopy = (() => {
    switch (reason) {
      case "expired": return "Your sign-in link expired. Get a fresh one below.";
      case "already_used": return "That sign-in link was already used. Get a fresh one below.";
      case "missing_token":
      case "malformed":
      case "bad_signature": return "That sign-in link couldn't be verified. Get a fresh one below.";
      default: return "";
    }
  })();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError("");
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const API_BASE = window.REACT_APP_DEMO_REQUESTS_API || "";
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      const res = await fetch(`${API_BASE}/api/trial/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.status === 429) {
        setError("Too many resend requests right now. Please wait a few minutes and try again.");
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (err) {
      const msg = err?.name === "AbortError"
        ? "Request timed out. Please try again."
        : (err?.message || "Could not send a new link. Please try again.");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App sample-requested-page">
      <header className="dbx-header" role="banner">
        <div className="dbx-header-inner">
          <Link to="/" className="dbx-brand" aria-label="Zerivo home">
            <img className="dbx-brand-mark" src={Logo} alt="" />
            <div className="dbx-brand-text-group">
              <span className="dbx-brand-text">Zerivo</span>
              <span className="dbx-brand-subtitle">Private AI</span>
            </div>
          </Link>
          <nav className="dbx-header-nav" aria-label="Main navigation">
            <Link to="/our-team">Our Team</Link>
            <Link to="/contact">Contact</Link>
          </nav>
        </div>
      </header>

      <main className="sample-requested-shell">
        <section className="sample-requested-hero" aria-labelledby="resend-title">
          <div className="sample-requested-copy">
            <div className="sample-requested-kicker">Resend sign-in link</div>
            <h1 id="resend-title">
              {submitted ? "Check your work email." : "Get a new sign-in link."}
            </h1>

            {!submitted && (
              <>
                {reasonCopy ? <p>{reasonCopy}</p> : (
                  <p>Sign-in links expire 15 minutes after they're sent and can only be used once. Drop your email below and we'll send you a fresh one.</p>
                )}

                <form className="trial-resend-form" onSubmit={handleSubmit} noValidate>
                  <label htmlFor="resend-email">
                    <span>Work email</span>
                    <input
                      ref={inputRef}
                      id="resend-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@yourorg.gov"
                      disabled={submitting}
                    />
                  </label>

                  {error ? <div className="trial-resend-error" role="alert">{error}</div> : null}

                  <button type="submit" className="dbx-btn dbx-btn-primary" disabled={submitting}>
                    {submitting ? "Sending..." : "Send me a new link"}
                  </button>
                </form>
              </>
            )}

            {submitted && (
              <p>
                If <strong>{email}</strong> has an active Zerivo trial, a new sign-in link is on its way.
                The link is valid for 15 minutes. Check spam or your security quarantine if it doesn't arrive.
              </p>
            )}
          </div>

          <aside className="sample-requested-panel" aria-label="Tips">
            <div className="sample-panel-label">A couple of tips</div>
            <ul>
              <li>Each sign-in link is single-use — clicking the same email twice won't work.</li>
              <li>Use the email you originally signed up with, not a forwarded address.</li>
              <li>If a teammate invited you, ask them which email is on the trial.</li>
              <li>Brand-new to Zerivo? <a href="https://portal.azure.com/#view/Microsoft_Azure_Marketplace/MarketplaceOffersBlade/selectedMenuItemId/home" target="_blank" rel="noopener noreferrer" style={{ color: "#fff", textDecoration: "underline" }}>Try it via Azure Marketplace</a>.</li>
            </ul>
          </aside>
        </section>

        <section className="sample-requested-card" aria-labelledby="resend-boundary-title">
          <div>
            <h2 id="resend-boundary-title">Why we send a link instead of a password</h2>
            <p>
              Trial workspaces are issued per email so each user gets their own audit trail. The 15-minute single-use link proves you control the inbox you signed up with — without you having to manage another password.
            </p>
          </div>
          <div className="sample-requested-actions">
            <Link to="/" className="dbx-btn dbx-btn-ghost">Return home</Link>
            <Link to="/contact?intent=trial-help" className="dbx-btn dbx-btn-ghost">Talk to us</Link>
          </div>
        </section>
      </main>
    </div>
  );
}
